import React from "react";
import LoadingSpinner from "./LoadingSpinner";
import { useTranslation } from "react-i18next";
import { LatestData } from "../List";
import { Card, Row, Col, Table } from "react-bootstrap";

const LatestTab = ({ data }: { data: LatestData | null }) => {
  const { t } = useTranslation();

  if (!data) return <LoadingSpinner />;

  const formatDate = (iso: string) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleDateString();
  };

  return (
    <Row className="g-4">
      {/* Latest Coaches */}
      <Col xl={6}>
        <Card className="h-100">
          <Card.Header>
            <h6 className="mb-0">{t("statistics.latestCoaches")}</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover borderless className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>{t("statistics.name")}</th>
                    <th>{t("statistics.email")}</th>
                    <th>{t("statistics.registered")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.latestCoaches ?? []).map((c) => (
                    <tr key={c.id}>
                      <td>
                        {c.firstName} {c.lastName}
                      </td>
                      <td className="text-muted">{c.email}</td>
                      <td>{formatDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Latest Companies */}
      <Col xl={6}>
        <Card className="h-100">
          <Card.Header>
            <h6 className="mb-0">{t("statistics.latestCompanies")}</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover borderless className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>{t("statistics.name")}</th>
                    <th>{t("statistics.email")}</th>
                    <th>{t("statistics.registered")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.latestCompanies ?? []).map((c) => (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td className="text-muted">{c.email}</td>
                      <td>{formatDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Latest Jobs */}
      <Col xl={6}>
        <Card className="h-100">
          <Card.Header>
            <h6 className="mb-0">{t("statistics.latestJobs")}</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover borderless className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>{t("statistics.title")}</th>
                    <th>{t("statistics.company")}</th>
                    <th>{t("statistics.location")}</th>
                    <th>{t("statistics.date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.latestJobs ?? []).map((j) => (
                    <tr key={j.id}>
                      <td>{j.title}</td>
                      <td className="text-muted">{j.company?.name ?? "-"}</td>
                      <td className="text-muted">{j.location?.name ?? "-"}</td>
                      <td>{formatDate(j.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Latest Matches */}
      <Col xl={6}>
        <Card className="h-100">
          <Card.Header>
            <h6 className="mb-0">{t("statistics.latestMatches")}</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover borderless className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>{t("statistics.coach")}</th>
                    <th>{t("statistics.job")}</th>
                    <th>{t("statistics.company")}</th>
                    <th>{t("statistics.date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.latestMatches ?? []).map((m) => (
                    <tr key={m.id}>
                      <td>
                        {m.coach
                          ? `${m.coach.firstName} ${m.coach.lastName}`
                          : "-"}
                      </td>
                      <td className="text-muted">{m.job?.title ?? "-"}</td>
                      <td className="text-muted">
                        {m.job?.company?.name ?? "-"}
                      </td>
                      <td>{formatDate(m.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Featured Jobs */}
      <Col xl={6}>
        <Card className="h-100">
          <Card.Header>
            <h6 className="mb-0">{t("statistics.latestFeatured")}</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover borderless className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>{t("statistics.title")}</th>
                    <th>{t("statistics.company")}</th>
                    <th>{t("statistics.location")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.latestFeatured ?? []).map((j) => (
                    <tr key={j.id}>
                      <td>{j.title}</td>
                      <td className="text-muted">{j.company?.name ?? "-"}</td>
                      <td className="text-muted">{j.location?.name ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Last Active Coaches */}
      <Col xl={6}>
        <Card className="h-100">
          <Card.Header>
            <h6 className="mb-0">{t("statistics.lastActiveCoaches")}</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover borderless className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>{t("statistics.name")}</th>
                    <th>{t("statistics.email")}</th>
                    <th>{t("statistics.lastActive")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.lastActiveCoaches ?? []).map((c) => (
                    <tr key={c.id}>
                      <td>
                        {c.firstName} {c.lastName}
                      </td>
                      <td className="text-muted">{c.email}</td>
                      <td>{formatDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Last Active Companies */}
      <Col xl={12}>
        <Card>
          <Card.Header>
            <h6 className="mb-0">{t("statistics.lastActiveCompanies")}</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover borderless className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>{t("statistics.name")}</th>
                    <th>{t("statistics.email")}</th>
                    <th>{t("statistics.lastActive")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.lastActiveCompanies ?? []).map((c) => (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td className="text-muted">{c.email}</td>
                      <td>{formatDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default LatestTab;
